import {
  ArrowLeft2,
  ArrowRight2,
  Edit,
  SearchNormal,
  Sort,
  Trash,
} from "iconsax-react";
import React, { useEffect, useState } from "react";
import { GripVertical, Search, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Link,
  useLoaderData,
  useNavigate,
  useRevalidator,
} from "react-router-dom";
import { PaginationButton } from "../../../../../components/PaginationButton";
import instance from "../../../../../axios/instance";
import { getToken } from "../../../../../utils/token";

export const treatmentsLoader = async ({ params }) => {
  const token = getToken();
  const data = await instance.get("/doctor/clinic/treatments", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return data;
};

export const TreatmentsPage = () => {
  const response = useLoaderData();
  const [treatmentList, setTreatmentList] = useState(response.data.map((_, index) => _.order_index ? _ : ({ ..._, order_index: index+1 })));
  const [searchQuery, setSearchQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const [sorted, setSorted] = useState("");
  const [filteredList, setFilteredList] = useState(
    treatmentList.filter((_, index) => index + 1 <= 10)
  );
  const [draggedItem, setDraggedItem] = useState(null);
  const [tempOrder, setTempOrder] = useState([]);
  const navigate = useNavigate();
  const revalidator = useRevalidator();

  const handleSearch = (e) => {
    e.preventDefault();

    const query = searchQuery.toLowerCase();
    setFilteredList(() => {
      return treatmentList
        .filter((treatment) => {
          const id = `${treatment.id}`;
          const title = treatment.title.toLowerCase().includes(query);
          const drugId = treatment.drug_id.toLowerCase().includes(query);
          const subtitle = treatment.subtitle.toLowerCase().includes(query);

          return id === query || title || drugId || subtitle;
        })
        .filter((_, index) => index + 1 <= 10);
    });
  };

  useEffect(() => {
    setFilteredList(() =>
      treatmentList.filter(
        (_, index) =>
          index + 1 > 10 * (pageIndex - 1) && index + 1 <= 10 * pageIndex
      )
    );
  }, [pageIndex]);

  const handleSort = (def) => {
    switch (def) {
      case "index": {
        setPageIndex(1);
        if (sorted !== "index") {
          setSorted("index");
        } else {
          setSorted("");
        }
        treatmentList.reverse();
        setTreatmentList(treatmentList);
        setFilteredList((items) =>
          treatmentList.filter((_, index) => index + 1 <= 10)
        );
        return;
      }
      case "title": {
        setPageIndex(1);
        if (sorted === "title") {
          setSorted("");
          treatmentList.sort((a, b) => a.index - b.index);
        } else {
          setSorted("title");
          treatmentList.sort((a, b) => {
            if (a.title < b.title) return -1;
            if (a.title > b.title) return 1;
            return 0;
          });
        }
        setFilteredList((items) =>
          treatmentList.filter((_, index) => index + 1 <= 10)
        );
        return;
      }
      case "date": {
        setPageIndex(1);
        if (sorted === "date") {
          setSorted("");
          treatmentList.sort((a, b) => a.index - b.index);
        } else {
          setSorted("date");
          treatmentList.sort(
            (a, b) => new Date(a.created_at) - new Date(b.created_at)
          );
        }
        setFilteredList((items) =>
          treatmentList.filter((_, index) => index + 1 <= 10)
        );
        return;
      }
    }
  };

  useEffect(() => {
    setFilteredList(() => treatmentList.filter((_, index) => index + 1 <= 10));
  }, [treatmentList]);

  useEffect(() => {
    setTreatmentList(() =>
      response.data.map((_, index) =>
        _.order_index ? _ : { ..._, order_index: index + 1 }
      )
    );
  }, [response.data]);

  const handleDragStart = (treatment) => {
    setFilteredList(() => treatmentList.filter((_, index) => index + 1 <= 10));
    setDraggedItem(treatment);
  };

  const updateTreatmentsOrder = async (updatedTreatments) => {
    try {
      const token = getToken();
      await instance.put(
        "/doctor/clinic/treatments/index-order",
        {
          treatments: updatedTreatments.map((t) => ({
            id: t.id,
            index: t.order_index,
          })),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      revalidator.revalidate();
    } catch (error) {
      revalidator.revalidate();
    }
  };

  const handleDragOver = (e, targetTreatment) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.id === targetTreatment.id) return;

    const updatedTreatments = treatmentList.map((t) => {
      if (t.id === draggedItem.id) {
        return { ...t, order_index: targetTreatment.order_index };
      }
      if (t.id === targetTreatment.id) {
        return { ...t, order_index: draggedItem.order_index };
      }
      return t;
    });

    setTempOrder(
      updatedTreatments.sort((a, b) => a.order_index - b.order_index)
    );
  };

  const handleDragEnd = () => {
    if (draggedItem) {
      setTreatmentList(tempOrder);
      updateTreatmentsOrder(tempOrder);
      setDraggedItem(null);
    }
  };

  return (
    <div className="px-7 py-5 w-full">
      <div className="flex justify-between items-center">
        <div className="font-medium text-xl mb-4">All Treatments</div>
        <Link
          to="/dashboard/settings/treatment-setup/treatments/new"
          className="bg-primary py-2 px-3 shadow-sm rounded-md font-medium text-[14px] hover:opacity-80 text-white"
        >
          New Treatment +
        </Link>
      </div>
      <div className="border bg-white rounded-md overflow-x-scroll scrollbar-hide mt-4">
        <div className="flex justify-between p-2 items-center">
          <form className="relative" onSubmit={handleSearch}>
            <input
              className="outline-none h-[40px] w-[300px] border rounded-md p-2 placeholder:font-normal placeholder:text-[14px] text-sm"
              placeholder="Search treatment by name/email/id"
              name="query"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button
              className="absolute h-[38px] rounded-md top-1/2 -translate-y-1/2 right-[1px] w-[30px] flex items-center justify-center bg-white cursor-pointer hover:text-primary duration-300"
              type="submit"
            >
              <SearchNormal size={15} />
            </button>
          </form>

          <div className="flex gap-2 items-center">
            <PaginationButton
              item={<ArrowLeft2 size={16} />}
              disabled={pageIndex <= 1}
              handleClick={() => setPageIndex(pageIndex - 1)}
            />
            <span className="font-light text-xs">Page {pageIndex}</span>
            <PaginationButton
              item={<ArrowRight2 size={16} />}
              disabled={pageIndex >= Math.ceil(treatmentList.length / 10)}
              handleClick={() => setPageIndex((index) => index + 1)}
            />
          </div>
        </div>
        <div className="max-h-[70vh] overflow-y-scroll scrollbar-hide">
          <table className="w-full relative whitespace-nowrap">
            <thead>
              <tr className="uppercase h-14 font-bold bg-[#f1f1f1] text-gray-700 text-[12px]">
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("index")}
                >
                  #{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "index" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td className="px-6 py-4">ID</td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("title")}
                >
                  Title{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "title" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td className="px-6 py-4">In Stock</td>
                <td className="px-6 py-4">Status</td>
                <td className="px-6 py-4" onClick={() => handleSort("date")}>
                  Date Created{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "date" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td className="px-6 py-4"></td>
              </tr>
            </thead>

            <tbody>
              {filteredList.length < 1 ? (
                <tr>
                  <td className="px-6 py-4 font-normal" colSpan={6}>
                    You do not have any treatments yet
                  </td>
                </tr>
              ) : (
                filteredList.map((treatment, index) => (
                  <tr
                    key={treatment.id}
                    className="h-14 text-[14px] font-normal border-b cursor-move"
                    draggable
                    onDragStart={() => handleDragStart(treatment)}
                    onDragOver={(e) => handleDragOver(e, treatment)}
                    onDragEnd={handleDragEnd}
                  >
                    <td className="px-6 py-4 flex items-center gap-2">
                      <GripVertical className="h-4 w-4 text-gray-400" />
                      {treatment.order_index}
                    </td>
                    <td className="px-6 py-4">
                      <Link
                        to={`${treatment.id}`}
                        className="underline text-primary"
                      >
                        {treatment.id}
                      </Link>
                    </td>
                    <td className="px-6 py-4">{treatment.title}</td>
                    <td className="px-6 py-4">
                      <div
                        className={`py-1 px-3 rounded-md border max-w-fit ${
                          treatment.instock === "true"
                            ? "border-primary text-primary"
                            : "border-[tomato] text-[tomato]"
                        }`}
                      >
                        {treatment.instock === "true"
                          ? "Instock"
                          : "Out of stock"}
                      </div>
                    </td>
                    <td className="px-6 py-4 capitalize">
                      {treatment.published === "true"
                        ? "Published"
                        : "Unpublished"}
                    </td>
                    <td className="px-6 py-4">
                      {new Date(treatment.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 underline cursor-pointer duration-300 hover:text-primary">
                      <Edit
                        onClick={() => {
                          navigate(
                            `/dashboard/settings/treatment-setup/treatments/${treatment.id}`
                          );
                        }}
                        size={24}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
